<template>
  <Dialog
    :model-value="visible"
    title="Preview Workflow"
    dialog-type="primary"
    dialog-size="large"
    title-variant="medium"
    :close-on-overlay="false"
    @update:model-value="(v) => { if (!v) handleClose() }"
    @close="handleClose"
  >
    <template #header-icon>
      <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="none">
        <path d="M20.5833 3.25H5.41667C4.21417 3.25 3.25 4.225 3.25 5.41667V20.5833C3.25 21.775 4.21417 22.75 5.41667 22.75H20.5833C21.775 22.75 22.75 21.775 22.75 20.5833V5.41667C22.75 4.225 21.7858 3.25 20.5833 3.25ZM20.5833 20.5833H5.41667V7.58333H20.5833V20.5833ZM13 11.375C14.9933 11.375 16.77 12.415 17.7017 14.0833C16.77 15.7517 14.9933 16.7917 13 16.7917C11.0067 16.7917 9.23 15.7517 8.29833 14.0833C9.23 12.415 11.0067 11.375 13 11.375ZM13 9.75C10.0425 9.75 7.51833 11.5483 6.5 14.0833C7.51833 16.6183 10.0425 18.4167 13 18.4167C15.9575 18.4167 18.4817 16.6183 19.5 14.0833C18.4817 11.5483 15.9575 9.75 13 9.75ZM13 15.7083C12.1008 15.7083 11.375 14.9825 11.375 14.0833C11.375 13.1842 12.1008 12.4583 13 12.4583C13.8992 12.4583 14.625 13.1842 14.625 14.0833C14.625 14.9825 13.8992 15.7083 13 15.7083Z" fill="white"/>
      </svg>
    </template>
    <div class="dialog-body">
        <!-- Loading State -->
        <div v-if="isLoading" class="loading-container">
          <div class="loading-spinner"></div>
          <p class="loading-text">Loading workflow...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-container">
          <p class="error-text">{{ error }}</p>
        </div>

        <!-- Workflow Canvas -->
        <div v-else class="workflow-preview-container">
        <!-- Custom Zoom Controls (Centered at top) -->
        <div class="canvas-toolbar-centered">
          <div class="zoom-controls">
            <button class="zoom-btn" @click="zoomOut">−</button>
            <span class="zoom-level">{{ currentZoom }}%</span>
            <button class="zoom-btn" @click="zoomIn">+</button>
          </div>
        </div>

        <!-- VueFlow Canvas (Read-only) -->
        <VueFlow
          ref="vueFlowRef"
          v-model:nodes="nodes"
          v-model:edges="edges"
          class="vue-flow-canvas"
          :min-zoom="0.25"
          :max-zoom="2"
          :snap-to-grid="true"
          :snap-grid="[15, 15]"
          :nodes-draggable="false"
          :nodes-connectable="false"
          :elements-selectable="false"
          :fit-view-on-init="true"
          @pane-ready="onPaneReady"
        >
          <Background pattern-color="#aaa" :gap="16" />

          <!-- Custom Node Templates - Using Backend Enum Values -->
          <!-- Trigger Nodes - All update triggers use TriggerOrderUpdate component -->
          <template #node-order_update="nodeProps">
            <TriggerOrderUpdate v-bind="nodeProps" />
          </template>
          <template #node-appointment_update="nodeProps">
            <TriggerOrderUpdate v-bind="nodeProps" />
          </template>
          <template #node-document_update="nodeProps">
            <TriggerOrderUpdate v-bind="nodeProps" />
          </template>
          <template #node-report_update="nodeProps">
            <TriggerOrderUpdate v-bind="nodeProps" />
          </template>
          <template #node-task_update="nodeProps">
            <TriggerOrderUpdate v-bind="nodeProps" />
          </template>
          <template #node-internal_notes_update="nodeProps">
            <TriggerOrderUpdate v-bind="nodeProps" />
          </template>
          <template #node-new_patient_created="nodeProps">
            <TriggerOrderUpdate v-bind="nodeProps" />
          </template>
          <template #node-manual="nodeProps">
            <TriggerManual v-bind="nodeProps" />
          </template>
          <template #node-scheduled="nodeProps">
            <TriggerScheduled v-bind="nodeProps" />
          </template>
          
          <!-- Action Nodes -->
          <template #node-send_message="nodeProps">
            <ActionSendNotification v-bind="nodeProps" />
          </template>
          <template #node-make_appointment="nodeProps">
            <ActionAppointment v-bind="nodeProps" />
          </template>
          <template #node-assign_task="nodeProps">
            <ActionAssignTask v-bind="nodeProps" />
          </template>
          <template #node-send_document="nodeProps">
            <ActionSendDocuments v-bind="nodeProps" />
          </template>
          <template #node-place_order="nodeProps">
            <ActionPlaceOrder v-bind="nodeProps" />
          </template>
          <template #node-add_internal_notes="nodeProps">
            <ActionAddInternalNotes v-bind="nodeProps" />
          </template>

          <!-- Custom Edge Template for Logic Connection -->
          <template #edge-logicConnection="edgeProps">
            <CustomConnectionLogic v-bind="edgeProps" />
          </template>
        </VueFlow>
        </div>
    </div>

    <!-- Dialog Footer -->
    <template #footer>
      <div class="preview-workflow-footer">
        <ResponsiveButton
          button_type="filled"
          built_in_theme="dark"
          size="medium"
          display_name="Back"
          @click-button="handleClose"
        />
      </div>
    </template>
  </Dialog>
</template>

<script>
import { VueFlow, useVueFlow } from '@vue-flow/core';
import { Background } from '@vue-flow/background';
import { getWorkflowApi, convertBackendToFrontend } from '@/api/workflowManage';

// Import all node components from NodesConnections directory
import TriggerOrderUpdate from '../NodesConnections/TriggerOrderUpdate.vue';
import TriggerManual from '../NodesConnections/TriggerManual.vue';
import TriggerScheduled from '../NodesConnections/TriggerScheduled.vue';
import ActionSendNotification from '../NodesConnections/ActionSendNotification.vue';
import ActionAppointment from '../NodesConnections/ActionAppointment.vue';
import ActionAssignTask from '../NodesConnections/ActionAssignTask.vue';
import ActionSendDocuments from '../NodesConnections/ActionSendDocuments.vue';
import ActionPlaceOrder from '../NodesConnections/ActionPlaceOrder.vue';
import ActionAddInternalNotes from '../NodesConnections/ActionAddInternalNotes.vue';
import CustomConnectionLogic from '../NodesConnections/CustomConnectionLogic.vue';

export default {
  name: 'PreviewWorkflowDialog',
  components: {
    VueFlow,
    Background,
    TriggerOrderUpdate,
    TriggerManual,
    TriggerScheduled,
    ActionSendNotification,
    ActionAppointment,
    ActionAssignTask,
    ActionSendDocuments,
    ActionPlaceOrder,
    ActionAddInternalNotes,
    CustomConnectionLogic
  },
  provide() {
    return {
      isNodeButtonDisabled: () => true, // Always disabled in preview mode
      isProgram: () => false
    };
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    workflowId: {
      type: String,
      required: true
    }
  },
  emits: ['update:modelValue', 'close'],
  setup() {
    const { zoomIn, zoomOut, fitView, getViewport } = useVueFlow();
    return { vueFlowZoomIn: zoomIn, vueFlowZoomOut: zoomOut, fitView, getViewport };
  },
  data() {
    return {
      nodes: [],
      edges: [],
      isLoading: false,
      error: null
    };
  },
  computed: {
    visible: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      }
    },
    currentZoom() {
      // Get current zoom from VueFlow viewport
      const viewport = this.getViewport();
      return viewport ? Math.round(viewport.zoom * 100) : 100;
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.workflowId) {
        this.loadWorkflow();
      }
    }
  },
  methods: {
    async loadWorkflow() {
      if (!this.workflowId) {
        this.error = 'No workflow ID provided';
        return;
      }

      this.isLoading = true;
      this.error = null;

      try {
        console.log('🔍 Loading workflow for preview:', this.workflowId);
        
        // Call API to get workflow data
        const response = await getWorkflowApi(this.workflowId);
        console.log('📊 Workflow API response:', response.data);

        if (response.data) {
          // Convert backend data to frontend format
          const convertedData = convertBackendToFrontend(response.data);
          console.log('✅ Converted workflow data:', convertedData);

          // Set nodes and edges
          this.nodes = convertedData.nodes || [];
          this.edges = convertedData.edges || [];

          // Fit view after data is loaded
          this.$nextTick(() => {
            if (this.vueFlowInstance) {
              this.vueFlowInstance.fitView({ padding: 0.2 });
            }
          });
        } else {
          this.error = 'No workflow data received';
        }
      } catch (error) {
        console.error('❌ Error loading workflow:', error);
        this.error = error.response?.data?.message || 'Failed to load workflow';
      } finally {
        this.isLoading = false;
      }
    },
    onPaneReady() {
      // Fit view on initial load
      this.$nextTick(() => {
        if (this.nodes.length > 0) {
          this.fitView({ padding: 0.2 });
        }
      });
    },
    zoomIn() {
      this.vueFlowZoomIn({ duration: 200 });
    },
    zoomOut() {
      this.vueFlowZoomOut({ duration: 200 });
    },
    handleClose() {
      this.visible = false;
      this.$emit('close');
    }
  }
};
</script>

<style scoped>
/* Dialog Body */
.dialog-body {
  padding: 0;
  background: #F9FBFD;
  min-height: 600px;
  position: relative;
}

.preview-workflow-footer {
  width: 100%;
  display: flex;
  justify-content: center;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 600px;
  gap: 16px;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #E5E7EB;
  border-top-color: #004879;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #666666;
}

/* Error State */
.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 600px;
}

.error-text {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #EF4444;
}

/* Workflow Preview Container */
.workflow-preview-container {
  position: relative;
  height: 600px;
  background: #F9FBFD;
}

/* Canvas Toolbar (Centered at top) */
.canvas-toolbar-centered {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 8px;
  background: transparent;
  z-index: 10;
}

/* Zoom Controls */
.zoom-controls {
  display: flex;
  align-items: center;
  gap: 0;
  height: 32px;
  background: #FFFFFF;
  border: 1px solid #C4C4C4;
  border-radius: 5px;
  overflow: hidden;
  width: 135px;
  box-shadow: 0px 1px 3px 0px rgba(74, 74, 74, 0.25);
}

.zoom-btn {
  width: 32px;
  height: 100%;
  background: #FFFFFF;
  border: none;
  border-right: 1px solid #C4C4C4;
  font-size: 16px;
  color: #374957;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoom-btn:last-child {
  border-right: none;
  border-left: 1px solid #C4C4C4;
}

.zoom-btn:hover {
  background: #F3F4F6;
}

.zoom-level {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.4px;
  color: #666;
  text-align: center;
}

/* VueFlow Canvas */
.vue-flow-canvas {
  width: 100%;
  height: 100%;
}

</style>

<!-- VueFlow Core Styles - Must be unscoped -->
<style>
@import '@vue-flow/core/dist/style.css';
@import '@vue-flow/core/dist/theme-default.css';
</style>
